/**
 * Shared revenue-report money and month labels (WSRADM-0034).
 */

export function formatMoney(amount: number, currency: string, locale: string): string {
  if (currency.length > 0) {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch {
      // Invalid currency code — format as a number, never invent a symbol.
    }
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Axis-tick money: no cents, compact above 10,000 ($12K). Tooltips keep formatMoney. */
export function formatMoneyCompact(amount: number, currency: string, locale: string): string {
  const compact =
    Math.abs(amount) >= 10000 ? ({ notation: 'compact' } as const) : {};
  if (currency.length > 0) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
        ...compact,
      }).format(amount);
    } catch {
      // Invalid currency code — format as a number, never invent a symbol.
    }
  }
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    ...compact,
  }).format(amount);
}

/** Short month label, e.g. "Sep 2026". Malformed input returns the raw string. */
export function formatMonthLabel(month: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(
    new Date(year, monthIndex, 1),
  );
}

/** Long month label for breadcrumbs, e.g. "September 2026". */
export function formatMonthLong(month: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(year, monthIndex, 1),
  );
}

/** The calendar month before a `yyyy-MM` key, or `null` for a malformed key. */
export function previousMonth(month: string): string | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return null;
  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  if (monthNumber < 1 || monthNumber > 12) return null;
  const prevYear = monthNumber === 1 ? year - 1 : year;
  const prevMonthNumber = monthNumber === 1 ? 12 : monthNumber - 1;
  return `${String(prevYear).padStart(4, '0')}-${String(prevMonthNumber).padStart(2, '0')}`;
}

/**
 * The current calendar month as `yyyy-MM`, in the given IANA timezone (the report's own) —
 * falling back to the browser's timezone when it is empty or invalid.
 */
export function currentMonthKey(timezone: string, now: Date = new Date()): string {
  const read = (timeZone: string | undefined): string | null => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
      }).formatToParts(now);
      const year = parts.find((part) => part.type === 'year')?.value;
      const month = parts.find((part) => part.type === 'month')?.value;
      return year !== undefined && month !== undefined ? `${year}-${month}` : null;
    } catch {
      return null;
    }
  };
  return (
    (timezone.length > 0 ? read(timezone) : null) ??
    read(undefined) ??
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
}
